import React, { useEffect, useMemo, useState } from "react";
import { Button, Input, Spinner } from "reactstrap";

import ShellCard from "../../../../components/ShellCard";
import SectionCard from "../../../../components/SectionCard";
import { AUTOPILOT_COLORS } from "../../../../components/autopilot/styles";
import { formatDocument } from "../../../../utils/formatDoc";
import { MAX_CHAVES, nomeChaveValido, pareceChavePublica } from "../../../../components/AcessoSftp/sshKey";
import { linhasPastas } from "../helpers/pastas";

// Preview + formulário de confirmação (PO1-2167, revisto na PO1-3282):
// - Apelido editável SÓ para transportadora nova; existente é só leitura;
// - "Confirmar apelido" quando a existente está pendente de revisão;
// - vínculo inativo → checkbox "Religar o vínculo" obrigatório;
// - contatos: TI OBRIGATÓRIO (nome + e-mail), Logística e Cotação opcionais;
// - e-mail de aviso (um só), pré-preenchido com o sugerido ou com o do TI;
// - modo senha (gerada pelo sistema, sem campo) ou SSH (até 3 chaves nomeadas).
// `inicial` (vindo de um pedido da Curadoria) pré-preenche apelido, contatos e e-mail.

const ESTADO_ACESSO = {
  sem_acesso: { rotulo: "Sem acesso", cor: AUTOPILOT_COLORS.muted },
  gerenciado: { rotulo: "Acesso gerenciado", cor: AUTOPILOT_COLORS.statusSuccess },
  legado: { rotulo: "Usuário antigo (legado)", cor: AUTOPILOT_COLORS.statusWarning },
  pendente: { rotulo: "Operação em andamento", cor: AUTOPILOT_COLORS.statusWarning },
  revogado: { rotulo: "Acesso revogado", cor: AUTOPILOT_COLORS.statusDanger },
  indisponivel: { rotulo: "Wing FTP indisponível", cor: AUTOPILOT_COLORS.muted },
};

const PAPEIS = [
  { key: "ti", label: "TI (API/SFTP)", obrigatorio: true },
  { key: "logistica", label: "Logística (dia a dia)", obrigatorio: false },
  { key: "cotacao", label: "Cotação (comercial)", obrigatorio: false },
];
const CONTATO_VAZIO = { nome: "", email: "", telefone: "" };
const CONTATOS_VAZIOS = { ti: { ...CONTATO_VAZIO }, logistica: { ...CONTATO_VAZIO }, cotacao: { ...CONTATO_VAZIO } };

const rowStyle = { display: "flex", justifyContent: "space-between", gap: "1rem", padding: "0.35rem 0", fontSize: "0.86rem" };
const keyStyle = { color: AUTOPILOT_COLORS.muted };
const valStyle = { color: AUTOPILOT_COLORS.text, textAlign: "right" };
const labelStyle = { color: AUTOPILOT_COLORS.muted, fontSize: "0.76rem", marginBottom: "0.3rem", display: "block" };
const avisoStyle = (tone) => ({
  background: tone === "warning" ? AUTOPILOT_COLORS.statusWarningBg : AUTOPILOT_COLORS.insetSurface,
  border: `1px solid ${tone === "warning" ? AUTOPILOT_COLORS.statusWarningBorder : AUTOPILOT_COLORS.border}`,
  color: tone === "warning" ? AUTOPILOT_COLORS.statusWarning : AUTOPILOT_COLORS.text,
  borderRadius: 8,
  padding: "0.6rem 0.8rem",
  fontSize: "0.82rem",
  marginTop: "0.6rem",
});
const EMAIL_RE = /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/;

function Linha({ k, v }) {
  return (
    <div style={rowStyle}>
      <span style={keyStyle}>{k}</span>
      <span style={valStyle}>{v}</span>
    </div>
  );
}

function contatosIniciais(inicial, preview) {
  const base = { ti: { ...CONTATO_VAZIO }, logistica: { ...CONTATO_VAZIO }, cotacao: { ...CONTATO_VAZIO } };
  const fonte = (inicial && inicial.contatos) || (preview && preview.contatos) || null;
  if (fonte) {
    PAPEIS.forEach(({ key }) => {
      const c = fonte[key];
      if (c) base[key] = { nome: c.nome || "", email: c.email || "", telefone: c.telefone || "" };
    });
  }
  if (!base.ti.email && inicial && inicial.email) base.ti.email = inicial.email;
  return base;
}

export default function PreviewCard({ preview, acesso, onProvisionar, provisionando, inicial }) {
  const t = preview.transportadora;
  const nova = !t;
  const [fantasia, setFantasia] = useState("");
  const [confirmarApelido, setConfirmarApelido] = useState(false);
  const [religarVinculo, setReligarVinculo] = useState(false);
  const [modoAuth, setModoAuth] = useState("senha");
  const [email, setEmail] = useState("");
  const [chaves, setChaves] = useState([{ nome: "", chavePublica: "" }]);
  const [contatos, setContatos] = useState(CONTATOS_VAZIOS);

  useEffect(() => {
    const apelidoPedido = inicial && inicial.apelido;
    setFantasia(nova ? apelidoPedido || preview.autofill?.fantasia || preview.autofill?.razaoSocial || "" : t.fantasia || "");
    const c = contatosIniciais(inicial, preview);
    setContatos(c);
    setEmail(preview.emailSugerido || (inicial && inicial.email) || c.ti.email || "");
    setConfirmarApelido(false);
    setReligarVinculo(false);
  }, [preview, nova, t, inicial]);

  const estado = ESTADO_ACESSO[acesso?.estado] || ESTADO_ACESSO.sem_acesso;
  const vinculoInativo = Boolean(preview.vinculo?.existe && !preview.vinculo?.ativo);
  const ehFilial = t && t.ehMatriz === false;
  const pendente = Boolean(t && t.apelidoPendenteRevisao);

  const chavesValidas = useMemo(
    () => chaves.map((c) => ({ nome: nomeChaveValido(c.nome), chave: pareceChavePublica(c.chavePublica) })),
    [chaves]
  );
  const emailOk = EMAIL_RE.test(String(email || "").trim());
  const tiOk = contatos.ti.nome.trim().length >= 2 && EMAIL_RE.test(contatos.ti.email.trim());
  const opcionaisOk = ["logistica", "cotacao"].every((k) => {
    const c = contatos[k];
    const preenchido = c.nome.trim() || c.email.trim() || c.telefone.trim();
    return !preenchido || EMAIL_RE.test(c.email.trim());
  });
  const sshOk = modoAuth !== "ssh" || (chaves.length > 0 && chavesValidas.every((v) => v.nome && v.chave.ok));
  const podeProvisionar =
    !provisionando &&
    !ehFilial &&
    emailOk &&
    tiOk &&
    opcionaisOk &&
    sshOk &&
    (!nova || fantasia.trim().length >= 2) &&
    (!vinculoInativo || religarVinculo) &&
    (!pendente || confirmarApelido) &&
    acesso?.estado !== "pendente";

  const alterarChave = (i, campo, valor) =>
    setChaves((atual) => atual.map((c, idx) => (idx === i ? { ...c, [campo]: valor } : c)));

  // Lê o valor ANTES do updater: no React 16 o evento sintético volta ao pool.
  const alterarContato = (papel, campo, valor) =>
    setContatos((atual) => ({ ...atual, [papel]: { ...atual[papel], [campo]: valor } }));

  const contatosParaEnvio = () => {
    const saida = {};
    PAPEIS.forEach(({ key }) => {
      const c = contatos[key];
      const nome = c.nome.trim();
      const em = c.email.trim();
      const tel = c.telefone.trim();
      if (!nome && !em && !tel) return;
      saida[key] = { nome, email: em, telefone: tel || undefined };
    });
    return saida;
  };

  return (
    <ShellCard eyebrow="Preview" title={nova ? "Transportadora nova" : t.fantasia || t.razaoSocial} style={{ marginTop: "1rem" }}>
      <div className="row">
        <div className="col-md-6">
          <SectionCard title="Cadastro">
            <Linha k="CNPJ informado" v={formatDocument(preview.cnpj)} />
            <Linha k="Matriz" v={formatDocument(preview.cnpjMatriz)} />
            {t ? (
              <>
                <Linha k="Razão social" v={t.razaoSocial || "—"} />
                <Linha k="Situação" v={t.ativa ? "Ativa" : "Inativa"} />
                <Linha k="Papel" v={t.ehMatriz ? "Matriz" : "Filial"} />
              </>
            ) : (
              <>
                <Linha k="Receita" v={preview.autofill?.razaoSocial || "sem dados"} />
                <Linha k="Cadastro" v="Será criado na matriz" />
              </>
            )}
            <Linha
              k="Vínculo"
              v={!preview.vinculo?.existe ? "Será criado" : preview.vinculo.ativo ? "Ativo" : "Inativo (removido da carteira)"}
            />
            {ehFilial ? (
              <div style={avisoStyle("warning")}>Esta transportadora é filial sob o embarcador. O acesso é da matriz: informe o CNPJ dela.</div>
            ) : null}
            {inicial && inicial.id ? (
              <div style={avisoStyle()}>
                Pedido #{inicial.id} do embarcador{inicial.embarcador ? ` ${inicial.embarcador}` : ""}: apelido e contatos vieram pré-preenchidos.
              </div>
            ) : null}
          </SectionCard>

          <SectionCard title="Pastas (CT / FT / OC)" style={{ marginTop: "0.75rem" }}>
            {linhasPastas(preview.pastas?.linhas).map((l) => (
              <Linha key={l.tipo} k={l.tipo} v={l.rotulo} />
            ))}
            {preview.raizVinculo ? (
              <div style={{ color: AUTOPILOT_COLORS.textSoft, fontSize: "0.78rem", marginTop: "0.5rem", wordBreak: "break-all" }}>
                <code style={{ color: AUTOPILOT_COLORS.text }}>{preview.raizVinculo}</code>
              </div>
            ) : null}
            {preview.raizDivergente ? (
              <div style={avisoStyle("warning")}>Linhas com raízes diferentes: {preview.raizDivergente.join(" · ")}. Corrija antes de iniciar.</div>
            ) : null}
          </SectionCard>

          <SectionCard title="Acesso no Wing FTP" style={{ marginTop: "0.75rem" }}>
            <Linha k="Estado" v={<span style={{ color: estado.cor, fontWeight: 600 }}>{estado.rotulo}</span>} />
            {acesso?.usuario ? <Linha k="Usuário" v={<code style={{ color: AUTOPILOT_COLORS.text }}>{acesso.usuario}</code>} /> : null}
            {acesso?.estado === "legado" ? (
              <div style={avisoStyle()}>
                Já existe um usuário antigo na pasta deste vínculo. Iniciar não cria outro usuário nem emite credencial; a senha só muda pelo botão "Gerar nova senha", abaixo.
              </div>
            ) : null}
            {acesso?.estado === "gerenciado" ? (
              <div style={avisoStyle()}>Acesso já gerenciado. Reexecutar só completa o que faltar (contatos, pastas, e-mail, portal).</div>
            ) : null}
          </SectionCard>
        </div>

        <div className="col-md-6">
          <SectionCard title="Confirmar e iniciar o onboarding">
            <label style={labelStyle}>Apelido {nova ? "(vira o nome da pasta)" : "(só leitura)"}</label>
            <Input value={fantasia} onChange={(e) => setFantasia(e.target.value)} readOnly={!nova} disabled={!nova} maxLength={60} />
            {pendente ? (
              <label style={{ ...labelStyle, marginTop: "0.6rem", color: AUTOPILOT_COLORS.text }}>
                <input type="checkbox" checked={confirmarApelido} onChange={(e) => setConfirmarApelido(e.target.checked)} />{" "}
                Confirmar este apelido (está pendente de revisão)
              </label>
            ) : null}
            {vinculoInativo ? (
              <div style={avisoStyle("warning")}>
                <label style={{ margin: 0, color: AUTOPILOT_COLORS.statusWarning }}>
                  <input type="checkbox" checked={religarVinculo} onChange={(e) => setReligarVinculo(e.target.checked)} />{" "}
                  Religar o vínculo (removido da carteira). As pastas desligadas voltam junto.
                </label>
              </div>
            ) : null}

            <label style={{ ...labelStyle, marginTop: "0.9rem" }}>Contatos da transportadora com este embarcador</label>
            <div style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.78rem", marginBottom: "0.4rem" }}>
              TI é obrigatório (nome e e-mail): é quem recebe o acesso e o convite do portal. Logística e Cotação são opcionais.
            </div>
            {PAPEIS.map((papel) => (
              <div key={papel.key} style={{ display: "flex", gap: "0.4rem", alignItems: "center", marginBottom: "0.4rem", flexWrap: "wrap" }}>
                <span style={{ color: papel.obrigatorio ? AUTOPILOT_COLORS.text : AUTOPILOT_COLORS.muted, fontSize: "0.8rem", minWidth: 150 }}>
                  {papel.label}
                  {papel.obrigatorio ? " *" : ""}
                </span>
                <Input
                  bsSize="sm"
                  placeholder="Nome"
                  value={contatos[papel.key].nome}
                  onChange={(e) => { const v = e.target.value; alterarContato(papel.key, "nome", v); }}
                  maxLength={60}
                  style={{ flex: "1 1 120px" }}
                />
                <Input
                  bsSize="sm"
                  type="email"
                  placeholder="E-mail"
                  value={contatos[papel.key].email}
                  onChange={(e) => { const v = e.target.value; alterarContato(papel.key, "email", v); }}
                  maxLength={100}
                  style={{ flex: "1 1 160px" }}
                />
                <Input
                  bsSize="sm"
                  placeholder="Telefone"
                  value={contatos[papel.key].telefone}
                  onChange={(e) => { const v = e.target.value.replace(/[^\d() +-]/g, "").slice(0, 20); alterarContato(papel.key, "telefone", v); }}
                  style={{ flex: "0 1 130px" }}
                />
              </div>
            ))}
            {!tiOk ? <div style={{ color: AUTOPILOT_COLORS.statusWarning, fontSize: "0.76rem" }}>Informe nome e e-mail válido do contato de TI.</div> : null}

            <label style={{ ...labelStyle, marginTop: "0.9rem" }}>Autenticação</label>
            <div style={{ display: "flex", gap: "1rem", fontSize: "0.86rem", color: AUTOPILOT_COLORS.text }}>
              <label style={{ margin: 0 }}>
                <input type="radio" name="modoAuth" checked={modoAuth === "senha"} onChange={() => setModoAuth("senha")} /> Usuário e senha
              </label>
              <label style={{ margin: 0 }}>
                <input type="radio" name="modoAuth" checked={modoAuth === "ssh"} onChange={() => setModoAuth("ssh")} /> Chave SSH
              </label>
            </div>
            <div style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.78rem", marginTop: "0.3rem" }}>
              {modoAuth === "senha"
                ? "A senha é gerada pelo sistema e fica visível em \"Mostrar senha\" depois de criada."
                : `Cole até ${MAX_CHAVES} chaves públicas (ssh-rsa ≥ 2048 ou ssh-ed25519). Nenhuma chave privada passa pela Everlog.`}
            </div>

            {modoAuth === "ssh" ? (
              <div style={{ marginTop: "0.6rem" }}>
                {chaves.map((c, i) => (
                  <div key={i} style={{ marginBottom: "0.6rem" }}>
                    <Input
                      placeholder="Nome da chave (ex.: servidor TI)"
                      value={c.nome}
                      onChange={(e) => { const v = e.target.value; alterarChave(i, "nome", v); }}
                      maxLength={40}
                      style={{ marginBottom: "0.3rem" }}
                    />
                    <Input
                      type="textarea"
                      rows={3}
                      placeholder="ssh-rsa AAAA... ou bloco ---- BEGIN SSH2 PUBLIC KEY ----"
                      value={c.chavePublica}
                      onChange={(e) => { const v = e.target.value; alterarChave(i, "chavePublica", v); }}
                      style={{ fontFamily: "monospace", fontSize: "0.76rem" }}
                    />
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.76rem", marginTop: "0.2rem" }}>
                      <span style={{ color: chavesValidas[i].chave.ok ? AUTOPILOT_COLORS.statusSuccess : AUTOPILOT_COLORS.muted }}>
                        {c.chavePublica ? (chavesValidas[i].chave.ok ? `Formato ${chavesValidas[i].chave.formato}` : chavesValidas[i].chave.motivo) : ""}
                        {c.nome && !chavesValidas[i].nome ? " · nome inválido" : ""}
                      </span>
                      {chaves.length > 1 ? (
                        <Button color="link" size="sm" style={{ padding: 0 }} onClick={() => setChaves((a) => a.filter((_, idx) => idx !== i))}>
                          remover
                        </Button>
                      ) : null}
                    </div>
                  </div>
                ))}
                {chaves.length < MAX_CHAVES ? (
                  <Button color="secondary" size="sm" outline onClick={() => setChaves((a) => a.concat([{ nome: "", chavePublica: "" }]))}>
                    + outra chave
                  </Button>
                ) : null}
              </div>
            ) : null}

            <label style={{ ...labelStyle, marginTop: "0.9rem" }}>E-mail de aviso do acesso (obrigatório, um só)</label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="ti@transportadora.com.br" />
            <div style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.78rem", marginTop: "0.3rem" }}>
              {preview.emailSugerido ? "Sugerido pelo contato do vínculo (TI › logística › cotação) ou pelo usuário da transportadora no Hub." : "Pré-preenchido com o e-mail do TI; pode trocar."}{" "}
              O e-mail avisa que o acesso existe; a senha nunca vai por e-mail.
            </div>

            <div style={{ marginTop: "1rem", display: "flex", gap: "0.5rem", alignItems: "center" }}>
              <Button
                color="primary"
                disabled={!podeProvisionar}
                onClick={() =>
                  onProvisionar({
                    fantasia: nova ? fantasia.trim() : undefined,
                    confirmarApelido,
                    religarVinculo,
                    modoAuth,
                    email: email.trim(),
                    contatos: contatosParaEnvio(),
                    chaves: modoAuth === "ssh" ? chaves.map((c) => ({ nome: c.nome.trim(), chavePublica: c.chavePublica.trim() })) : undefined,
                  })
                }
              >
                {provisionando ? <Spinner size="sm" /> : "Iniciar onboarding"}
              </Button>
              {acesso?.estado === "pendente" ? (
                <span style={{ color: AUTOPILOT_COLORS.statusWarning, fontSize: "0.8rem" }}>Operação em andamento: consulte de novo em instantes.</span>
              ) : null}
            </div>
          </SectionCard>
        </div>
      </div>
    </ShellCard>
  );
}
